import React, { useId, useState } from 'react';
import { ArrowLeft, BookOpen, ChevronDown, X } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import { cn } from '@dashboard/lib/utils';
import GuideBadge from './GuideBadge';

const HEADING_CLASS = 'text-xs font-semibold leading-4 text-muted-foreground';

// Sits in the message box, so every button is type="button" or it would send the message.
export default function GuideCard({
  guide,
  defaultOpen = true,
  onUseExample,
  onBuildExample,
  isStreaming,
  onBack,
  onRemove,
}) {
  const titleId = useId();
  const examplesId = useId();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      role="article"
      aria-labelledby={titleId}
      className="@container overflow-hidden rounded-lg ring-1 ring-border"
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 py-1 pr-1',
          onBack ? 'pl-1' : 'pl-2.5',
        )}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Back to guides"
              className="text-muted-foreground"
              onClick={onBack}
            >
              <ArrowLeft aria-hidden="true" />
            </Button>
          )}
          <BookOpen aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
          <h3 id={titleId} className="truncate text-[13px] font-semibold leading-[18px]">
            {guide.section.label} guide
          </h3>
          {/* In a narrow chat the title says it all. */}
          <GuideBadge className="hidden @[20rem]:inline-flex">{guide.command}</GuideBadge>
        </div>
        <div className="flex shrink-0 items-center">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={isOpen ? 'Hide guide' : 'Show guide'}
              className="text-muted-foreground [&[data-state=open]>svg]:rotate-180"
            >
              <ChevronDown aria-hidden="true" className="transition-transform" />
            </Button>
          </CollapsibleTrigger>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Remove guide"
              className="text-muted-foreground"
              onClick={onRemove}
            >
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* Capped so the message box stays in view under a long guide. */}
      <CollapsibleContent className="flex max-h-[min(20rem,40vh)] flex-col gap-3 overflow-y-auto border-t border-border px-2.5 pb-2.5 pt-2">
        <div className="flex flex-col gap-1">
          <p className="text-[13px] leading-[19px] text-foreground">{guide.summary}</p>
          {guide.coverageNote && (
            <p className="text-xs leading-[17px] text-muted-foreground">{guide.coverageNote}</p>
          )}
        </div>

        <section aria-labelledby={examplesId}>
          <h4 id={examplesId} className={cn('mb-1.5', HEADING_CLASS)}>
            Say it like this
          </h4>
          <ul className="flex flex-col gap-1">
            {guide.examples.map(({ label, prompt }) => (
              <li
                key={label}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-md border border-border bg-background py-1.5 pl-2.5 pr-1.5"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 basis-40 rounded-sm text-left text-[13px] leading-[19px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => onUseExample(prompt)}
                >
                  {label}
                </button>
                {/* The message box cannot send while a reply streams, so neither can this. */}
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  aria-label={`Build this: ${label}`}
                  disabled={isStreaming}
                  onClick={() => onBuildExample(prompt)}
                >
                  Build this
                </Button>
              </li>
            ))}
          </ul>
        </section>
      </CollapsibleContent>
    </Collapsible>
  );
}
